import { DataLayerErrorHandlerCallback, EventDataMap, EventsData, SupportedEvents, WindowWithDataLayer } from '.'

import {
    CartViewModel,
    GetPaymentOptionsViewModel,
    GetProductListViewModel,
    GetSubCartViewModel,
    PaymentFinalStatusViewModel,
    ProductViewModel,
} from '@/types/api'
import { GetShippingOptionsViewModel } from '@/types/nrc-api'

import { getActiveExperiments } from '@/features/experiments/get-active-experiments'
import { CartActionPlacement } from '@/features/webengage/types'

import { PUBLIC_DOMAIN_URL } from '@/app/_constants/urls'
import { normalizeDestinationUrl } from '@/app/_utils/normalize-destination-url'

import {
    AddOrRemoveCartData,
    CtaClickEventData,
    RecommendedProductCtaClickData,
    RecommendedProductCtaClickParams,
    ViewItemType,
} from './types'
import {
    calculateCartTotalPrice,
    calculatePurchaseTotalValue,
    findCartsDiff,
    generateEventsCouponData,
    generateEvnetsItemsData,
} from './utils'

import { trackInhouseEvent } from '../in-house'

declare const window: WindowWithDataLayer<EventsData['event']>

const userExperiments = () => {
    const experiments = getActiveExperiments()
    return { experiment: experiments.length > 0 ? experiments : null }
}

function _pushEvent<T extends EventsData['event']>(
    eventName: T,
    data: EventDataMap[T],
    handleError?: DataLayerErrorHandlerCallback
): void {
    const event = {
        event: eventName,
        ...userExperiments(),
        ...data,
    }

    trackInhouseEvent(event)

    if (typeof window === 'undefined' || !window?.dataLayer) {
        handleError?.()

        return
    }

    window.dataLayer.push(event)
}

export function sendPageViewEvent(data: EventDataMap[SupportedEvents.pageview]): void {
    _pushEvent(SupportedEvents.pageview, data)
}

export function sendLocationNotDetectedEvent(): void {
    _pushEvent(SupportedEvents.location_not_detected, undefined)
}

export function sendUserAttributesEvent(data: EventDataMap[SupportedEvents.user_attributes]): void {
    _pushEvent(SupportedEvents.user_attributes, data)
}

export function sendUserIdEvent(data: EventDataMap[SupportedEvents.user_id]): void {
    _pushEvent(SupportedEvents.user_id, data)
}

export function sendPopUpEvent(data: EventDataMap[SupportedEvents.popUp]): void {
    _pushEvent(SupportedEvents.popUp, data)
}

export function sendViewItemEvent(data: ProductViewModel, listType: ViewItemType): void {
    _pushEvent(SupportedEvents.view_item, generateViewItemEventData(data, listType))
}

export function sendLoginEvent(): void {
    _pushEvent(SupportedEvents.login, {
        method: 'phonenumber',
    })
}

export function sendSignupEvent(): void {
    _pushEvent(SupportedEvents.sign_up, {
        method: 'phonenumber',
    })
}

export function sendCtaClickEvent(data: CtaClickEventData): void {
    _pushEvent(SupportedEvents.cta_click, generateCtaClickEventData(data))
}

export const generateCtaClickEventData = (data: CtaClickEventData): CtaClickEventData => {
    const destinationUrl = data?.destinationUrl ? normalizeDestinationUrl(data.destinationUrl) : null

    return {
        ...data,
        page_path: window.location.href,
        destinationUrl,
    }
}

export const generateViewItemListEventData = (
    lastPageData: GetProductListViewModel,
    categories: [string, string, string]
) => {
    const items = lastPageData.products?.items || []
    const lastPageNumber = lastPageData.products?.pageNumber || 1

    const eventData: EventDataMap[SupportedEvents.view_item_list] = {
        ecommerce: {
            items:
                items.map((product, index) => ({
                    item_name: product.nameFa ?? null,
                    item_id: product.id ?? null,
                    item_brand: product.brand?.nameFa ?? null,
                    item_category3: categories?.at(2) ?? '',
                    item_category2: categories?.at(1) ?? '',
                    item_category: categories?.at(0) ?? '',
                    price: product.discountPrice ?? product.basePrice ?? null,
                    index,
                    pageNumber: lastPageNumber,
                })) || [],
        },
    }

    return eventData
}

export const generateViewCartEventData = (cart: CartViewModel) => {
    const nonPromotionItems = cart.items?.filter((item) => !item.promotionOffer) || []

    const totalValue = calculateCartTotalPrice(nonPromotionItems)
    const eventItems = generateEvnetsItemsData(nonPromotionItems)

    const eventData: EventDataMap[SupportedEvents.view_cart] = {
        ecommerce: {
            currency: 'USD',
            value: totalValue,
            cart_id: cart.id || null,
            items: eventItems,
        },
    }

    return eventData
}

export const generateBeginCheckoutEventData = (cart: GetSubCartViewModel) => {
    const cartItems = cart.subCarts?.flatMap((subCart) => subCart.items || []) || []
    const nonPromotionItems = cartItems?.filter((item) => !item?.promotionOffer) || []
    const cartId = cart.subCarts?.[0]?.id ?? null

    const totalValue = calculateCartTotalPrice(nonPromotionItems)
    const eventItems = generateEvnetsItemsData(nonPromotionItems)
    const { coupon, coupon_amount } = generateEventsCouponData(cartItems, cart.promotion || null, null)

    const eventData: EventDataMap[SupportedEvents.begin_checkout] = {
        ecommerce: {
            currency: 'USD',
            value: totalValue,
            coupon,
            coupon_amount,
            cart_id: cartId,
            items: eventItems,
        },
    }

    return eventData
}

export const generateAddShippingInfoEventData = (
    cart: GetSubCartViewModel,
    shippingCount: number,
    khanoumiSubOrderShipping?: GetShippingOptionsViewModel
) => {
    const cartItems = cart.subCarts?.flatMap((subCart) => subCart.items || []) || []
    const nonPromotionItems = cartItems?.filter((item) => !item?.promotionOffer) || []
    const cartId = cart.subCarts?.[0]?.id ?? null
    const promotion = cart.promotion || null

    const totalValue = calculateCartTotalPrice(nonPromotionItems)
    const eventItems = generateEvnetsItemsData(nonPromotionItems)

    const khanoumiSubOrderShippingCost = khanoumiSubOrderShipping?.shippingMethodCost ?? null
    const { coupon, coupon_amount } = generateEventsCouponData(cartItems ?? [], promotion, khanoumiSubOrderShippingCost)

    const eventData: EventDataMap[SupportedEvents.add_shipping_info] = {
        ecommerce: {
            currency: 'USD',
            value: totalValue,
            coupon,
            coupon_amount,
            cart_id: cartId,
            shipping_tier: khanoumiSubOrderShipping?.shippingMethodTitle ?? null,
            shipment_count: shippingCount,
            items: eventItems,
        },
    }

    return eventData
}
export const generateAddPaymentInfoEventData = (
    cart: GetSubCartViewModel,
    khanoumiOrderShippingCost: number | null,
    payment: GetPaymentOptionsViewModel
) => {
    const cartItems = cart.subCarts?.flatMap((subCart) => subCart.items || []) || []
    const nonPromotionItems = cartItems?.filter((item) => !item?.promotionOffer) || []
    const cartId = cart.subCarts?.[0]?.id ?? null

    const totalValue = calculateCartTotalPrice(nonPromotionItems)
    const eventItems = generateEvnetsItemsData(nonPromotionItems)
    const { coupon, coupon_amount } = generateEventsCouponData(
        cartItems ?? [],
        cart.promotion || null,
        khanoumiOrderShippingCost
    )

    const fullyWithCredit = payment.amount === 0

    const eventData: EventDataMap[SupportedEvents.add_payment_info] = {
        ecommerce: {
            currency: 'USD',
            value: totalValue,
            coupon,
            coupon_amount,
            cart_id: cartId,
            payment_type: fullyWithCredit ? 'Credit' : payment.paymentMethodType || null,
            items: eventItems,
        },
    }

    return eventData
}

export const generatePurchaseEventData = (data: PaymentFinalStatusViewModel, paymentId: string) => {
    const nonPromotionItems = data.order?.subOrder?.lineItems?.filter((item) => !item.isPromotionOffer) || []

    const value = calculatePurchaseTotalValue(nonPromotionItems)

    const coupon = data.order?.promotionOfferType
        ? `${data.order?.promotionOfferType}-${data.order?.promotionTitle}`
        : null

    const eventData: EventDataMap[SupportedEvents.purchase] = {
        ecommerce: {
            currency: 'USD',
            value,
            coupon,
            coupon_amount: data.order?.totalPromotionAmount ?? null,
            shipping: data.order?.shippingCost ?? null,
            transaction_id: paymentId,
            order_id: data.order?.orderId ?? null,
            cart_id: data.order?.cartId ?? null,
            shipping_tier: data.order?.subOrder?.deliveryTypeTitle ?? null,
            shipment_count: data.shipmentCount ?? 0,
            payment_type: data.gateway ?? null,
            order_count: null,
            items:
                nonPromotionItems.map((item) => ({
                    item_id: item.product?.productId?.toString() ?? null,
                    item_name: item.product?.name ?? null,
                    coupon: item.hasPricingGift ? 'pricinggift' : null,
                    item_brand: item.product?.brand ?? null,
                    item_category: item.product?.categoryLevels?.level1?.nameFa ?? null,
                    item_category2: item.product?.categoryLevels?.level2?.nameFa ?? null,
                    item_category3: item.product?.categoryLevels?.level3?.nameFa ?? null,
                    item_category4: null,
                    item_category5: null,
                    item_variant: item.product?.color?.name ?? null,
                    price: item.price || 0,
                    discount: (item.hasPricingGift ? item.basePrice : item.unitDiscount) ?? 0,
                    quantity: item.quantity ?? 1,
                    ship_by_seller: item.shipBySeller ?? false,
                })) || [],
        },
    }

    return eventData
}

export const generateAddOrRemoveCartItemEventData = (
    type: SupportedEvents.add_to_cart | SupportedEvents.remove_from_cart,
    prevCart: CartViewModel,
    cart: CartViewModel,
    placement: CartActionPlacement,
    itemIndexInList?: number
) => {
    const prevItems = prevCart.items?.filter((item) => !item.promotionOffer) || []
    const curItems = cart.items?.filter((item) => !item.promotionOffer) || []

    const diffItems = findCartsDiff(type, prevItems, curItems)

    const totalValue = calculateCartTotalPrice(diffItems)

    const eventItems = generateEvnetsItemsData(diffItems, placement, itemIndexInList)

    const eventData: AddOrRemoveCartData = {
        ecommerce: {
            currency: 'USD',
            cart_id: prevCart.id ?? null,
            value: totalValue,
            items: eventItems,
        },
    }

    return eventData
}

const generateRecommendedProductCtaClickEventData = (
    eventData: RecommendedProductCtaClickParams
): RecommendedProductCtaClickData => {
    const { slug, ...restOfEventData } = eventData

    return {
        ...restOfEventData,
        page_path: window.location.href,
        destinationUrl: slug ? `${PUBLIC_DOMAIN_URL}/${slug}` : `${PUBLIC_DOMAIN_URL}`,
    }
}
export const sendViewItemListEvent = (lastPageData: GetProductListViewModel, categories: [string, string, string]) =>
    _pushEvent(SupportedEvents.view_item_list, generateViewItemListEventData(lastPageData, categories))

export const sendViewCartEvent = (cart: CartViewModel) =>
    _pushEvent(SupportedEvents.view_cart, generateViewCartEventData(cart))

export const sendBeginCheckoutEvent = (cart: GetSubCartViewModel) =>
    _pushEvent(SupportedEvents.begin_checkout, generateBeginCheckoutEventData(cart))

export const sendAddShippingInfoEvent = (
    cart: GetSubCartViewModel,
    shippingCount: number,
    khanoumiSubOrderShipping?: GetShippingOptionsViewModel
) =>
    _pushEvent(
        SupportedEvents.add_shipping_info,
        generateAddShippingInfoEventData(cart, shippingCount, khanoumiSubOrderShipping)
    )

export const sendAddPaymentInfoEvent = (
    cart: GetSubCartViewModel,
    khanoumiOrderShippingCost: number | null,
    payment: GetPaymentOptionsViewModel
) =>
    _pushEvent(
        SupportedEvents.add_payment_info,
        generateAddPaymentInfoEventData(cart, khanoumiOrderShippingCost, payment)
    )

export const sendPurchaseEvent = (
    data: PaymentFinalStatusViewModel,
    paymentId: string,
    handleError: DataLayerErrorHandlerCallback
) => _pushEvent(SupportedEvents.purchase, generatePurchaseEventData(data, paymentId), handleError)

export const sendAddOrRemoveCartItemEvent = (
    type: SupportedEvents.add_to_cart | SupportedEvents.remove_from_cart,
    prevCart: CartViewModel,
    cart: CartViewModel,
    placement: CartActionPlacement,
    itemIndexInList?: number
) => _pushEvent(type, generateAddOrRemoveCartItemEventData(type, prevCart, cart, placement, itemIndexInList))

export const generateViewItemEventData = (product: ProductViewModel, type: ViewItemType) => {
    const breadcrumb = product.breadcrumb || []
    const categoryMap = new Map(breadcrumb.map((b) => [b.level, b.nameFa]))

    const variants = product.variants || []

    if (!variants.length) {
        const eventData: EventDataMap[SupportedEvents.view_item] = {
            ecommerce: {
                currency: 'USD',
                value: product.salesPrice || 0,
                items: [
                    {
                        item_id: product.id || '',
                        item_name: product.nameFa || '',
                        discount: product.discountPrice || 0,
                        item_brand: product.brand?.nameFa || '',
                        item_category: categoryMap.get(1) || null,
                        item_category2: categoryMap.get(2) || null,
                        item_category3: categoryMap.get(3) || null,
                        item_category4: categoryMap.get(4) || null,
                        item_category5: categoryMap.get(5) || null,
                        price: product.salesPrice || 0,
                        quantity: 1,
                        item_variant: null,
                        coupon: null,
                        item_list_id: type,
                        item_list_name: type,
                        index: null,
                        ship_by_seller: false,
                    },
                ],
            },
        }

        return eventData
    }

    const value = variants.reduce((total, variant) => {
        return total + (variant.salesPrice || 0)
    }, 0)

    const eventData: EventDataMap[SupportedEvents.view_item] = {
        ecommerce: {
            currency: 'USD',
            value,
            items:
                variants.map((variant) => ({
                    item_id: variant.id || '',
                    item_name: product.nameFa || '',
                    discount: variant.discountPrice || 0,
                    item_brand: product.brand?.nameFa || '',
                    item_category: categoryMap.get(1) || null,
                    item_category2: categoryMap.get(2) || null,
                    item_category3: categoryMap.get(3) || null,
                    item_category4: categoryMap.get(4) || null,
                    item_category5: categoryMap.get(5) || null,
                    item_variant: variant.color?.name || null,
                    price: variant.salesPrice || 0,
                    quantity: 1,
                    coupon: null,
                    item_list_id: type,
                    item_list_name: type,
                    index: null,
                    ship_by_seller: variant.shipBySeller ?? false,
                })) || [],
        },
    }

    return eventData
}

export const sendRecommendedProductCtaClickEvent = (eventData: RecommendedProductCtaClickParams) =>
    _pushEvent(SupportedEvents.cta_click, generateRecommendedProductCtaClickEventData(eventData))
